import { request } from './client';
import type {
  DashboardStats,
  ListUsersResult,
  ListUsersInput,
  UserWithStats,
  Board,
  ListBoardsResult,
  ListBoardsInput,
  ListPaymentsResult,
  ListPaymentsInput,
  ListAuditLogsResult,
  RoleInfo,
  UserRole,
  User,
} from '../types';

// Dashboard
export async function getDashboardStats(): Promise<DashboardStats> {
  return request<DashboardStats>('/api/squareup/admin/stats');
}

// Users
export async function listUsers(input?: ListUsersInput): Promise<ListUsersResult> {
  const params = new URLSearchParams();
  if (input?.search) params.set('search', input.search);
  if (input?.role) params.set('role', input.role);
  if (input?.status) params.set('status', input.status);
  if (input?.page) params.set('page', input.page.toString());
  if (input?.pageSize) params.set('pageSize', input.pageSize.toString());
  if (input?.sortBy) params.set('sortBy', input.sortBy);
  if (input?.sortDir) params.set('sortDir', input.sortDir);

  const query = params.toString();
  return request<ListUsersResult>(`/api/squareup/admin/users${query ? `?${query}` : ''}`);
}

export async function getUser(userId: string): Promise<UserWithStats> {
  return request<UserWithStats>(`/api/squareup/admin/users/${userId}`);
}

export async function getUserBoards(userId: string): Promise<Board[]> {
  return request<Board[]>(`/api/squareup/admin/users/${userId}/boards`);
}

export async function updateUserRole(userId: string, role: UserRole): Promise<User> {
  return request<User>(`/api/squareup/admin/users/${userId}/role`, {
    method: 'PUT',
    body: { role },
  });
}

export async function disableUser(userId: string, reason?: string): Promise<User> {
  return request<User>(`/api/squareup/admin/users/${userId}/disable`, {
    method: 'POST',
    body: { reason },
  });
}

export async function enableUser(userId: string): Promise<User> {
  return request<User>(`/api/squareup/admin/users/${userId}/enable`, {
    method: 'POST',
    body: {},
  });
}

// Roles
export async function getRoles(): Promise<RoleInfo[]> {
  return request<RoleInfo[]>('/api/squareup/admin/roles');
}

// Boards
export async function listAllBoards(input?: ListBoardsInput): Promise<ListBoardsResult> {
  const params = new URLSearchParams();
  if (input?.search) params.set('search', input.search);
  if (input?.status) params.set('status', input.status);
  if (input?.includeDisabled) params.set('includeDisabled', 'true');
  if (input?.page) params.set('page', input.page.toString());
  if (input?.pageSize) params.set('pageSize', input.pageSize.toString());

  const query = params.toString();
  return request<ListBoardsResult>(`/api/squareup/admin/boards${query ? `?${query}` : ''}`);
}

export async function reassignBoard(boardId: string, newOwnerId: string): Promise<Board> {
  return request<Board>(`/api/squareup/admin/boards/${boardId}/reassign`, {
    method: 'POST',
    body: { newOwnerId },
  });
}

// Payments
export async function listAllPayments(input?: ListPaymentsInput): Promise<ListPaymentsResult> {
  const params = new URLSearchParams();
  if (input?.boardId) params.set('boardId', input.boardId);
  if (input?.userId) params.set('userId', input.userId);
  if (input?.entryType) params.set('entryType', input.entryType);
  if (input?.page) params.set('page', input.page.toString());
  if (input?.pageSize) params.set('pageSize', input.pageSize.toString());

  const query = params.toString();
  return request<ListPaymentsResult>(`/api/squareup/admin/payments${query ? `?${query}` : ''}`);
}

// Audit logs
export async function listAuditLogs(input?: {
  action?: string;
  actorId?: string;
  targetId?: string;
  page?: number;
  pageSize?: number;
}): Promise<ListAuditLogsResult> {
  const params = new URLSearchParams();
  if (input?.action) params.set('action', input.action);
  if (input?.actorId) params.set('actorId', input.actorId);
  if (input?.targetId) params.set('targetId', input.targetId);
  if (input?.page) params.set('page', input.page.toString());
  if (input?.pageSize) params.set('pageSize', input.pageSize.toString());

  const query = params.toString();
  return request<ListAuditLogsResult>(`/api/squareup/admin/audit${query ? `?${query}` : ''}`);
}

// Transaction types for admin
export interface AdminTransaction {
  id: string;
  userId: string;
  productId: string;
  priceId: string;
  stripePaymentIntentId: string;
  stripeChargeId?: string;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed';
  couponId?: string;
  discountAmount: number;
  failureCode?: string;
  failureMessage?: string;
  cardBrand?: string;
  cardLast4?: string;
  paymentMethodType?: string;
  receiptUrl?: string;
  mode: 'test' | 'live';
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    displayName: string;
  };
  product?: {
    id: string;
    name: string;
    type: string;
    metadata: {
      boardCredits?: number;
    };
  };
}

export interface AdminTransactionsResult {
  transactions: AdminTransaction[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ListTransactionsInput {
  userId?: string;
  status?: string;
  mode?: string;
  page?: number;
  pageSize?: number;
}

export interface RevenueStats {
  totalRevenue: number;
  totalTransactions: number;
  successfulPayments: number;
  failedPayments: number;
  pendingPayments: number;
  testRevenue: number;
  liveRevenue: number;
}

// Transactions (Stripe payments)
export async function listTransactions(input?: ListTransactionsInput): Promise<AdminTransactionsResult> {
  const params = new URLSearchParams();
  if (input?.userId) params.set('userId', input.userId);
  if (input?.status) params.set('status', input.status);
  if (input?.mode) params.set('mode', input.mode);
  if (input?.page) params.set('page', input.page.toString());
  if (input?.pageSize) params.set('pageSize', input.pageSize.toString());

  const query = params.toString();
  return request<AdminTransactionsResult>(`/api/squareup/admin/transactions${query ? `?${query}` : ''}`);
}

export async function getTransaction(transactionId: string): Promise<AdminTransaction> {
  return request<AdminTransaction>(`/api/squareup/admin/transactions/${transactionId}`);
}

// Revenue stats
export async function getRevenueStats(): Promise<RevenueStats> {
  return request<RevenueStats>('/api/squareup/admin/revenue');
}

// Admin Subscriptions
export interface AdminSubscription {
  id: string;
  userId: string;
  productId: string;
  priceId: string;
  stripeSubscriptionId: string;
  status: 'active' | 'past_due' | 'canceled' | 'unpaid';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  mode: 'test' | 'live';
  createdAt: string;
  user?: {
    id: string;
    email: string;
    displayName: string;
  };
  product?: {
    id: string;
    name: string;
  };
}

export interface AdminSubscriptionsResult {
  subscriptions: AdminSubscription[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListSubscriptionsInput {
  status?: string;
  page?: number;
  pageSize?: number;
}

export async function listSubscriptions(input?: ListSubscriptionsInput): Promise<AdminSubscriptionsResult> {
  const params = new URLSearchParams();
  if (input?.status) params.set('status', input.status);
  if (input?.page) params.set('page', input.page.toString());
  if (input?.pageSize) params.set('pageSize', input.pageSize.toString());

  const query = params.toString();
  return request<AdminSubscriptionsResult>(`/api/squareup/admin/subscriptions${query ? `?${query}` : ''}`);
}

// Payment Audit Logs
export interface PaymentAuditLog {
  id: string;
  userId: string;
  action: string;
  category: string;
  description: string;
  details: Record<string, unknown>;
  ipAddress: string;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    displayName: string;
  };
}

export interface PaymentAuditLogsResult {
  logs: PaymentAuditLog[];
  total: number;
}

export interface GetAuditLogsInput {
  action?: string;
  category?: string;
  userId?: string;
  limit?: number;
  offset?: number;
}

export async function getAuditLogs(input?: GetAuditLogsInput): Promise<PaymentAuditLogsResult> {
  const params = new URLSearchParams();
  if (input?.action) params.set('action', input.action);
  if (input?.category) params.set('category', input.category);
  if (input?.userId) params.set('userId', input.userId);
  if (input?.limit) params.set('limit', input.limit.toString());
  if (input?.offset) params.set('offset', input.offset.toString());

  const query = params.toString();
  return request<PaymentAuditLogsResult>(`/api/squareup/admin/audit-logs${query ? `?${query}` : ''}`);
}

// ============================================================================
// Admin Product Management
// ============================================================================

import type { PaymentProduct, PaymentPrice, ProductMeta, ProductType, BundleItem } from './checkout';

export type { PaymentProduct, PaymentPrice, ProductMeta, ProductType, BundleItem };

export interface BundleItemInput {
  productId: string;
  quantity: number;
}

export interface CreateProductInput {
  lookupKey: string;
  name: string;
  description: string;
  type: ProductType;
  metadata: ProductMeta;
  displayOrder?: number;
  bundleItems?: BundleItemInput[];
}

export interface UpdateProductInput {
  name?: string;
  description?: string;
  metadata?: ProductMeta;
  active?: boolean;
  displayOrder?: number;
}

export interface CreatePriceInput {
  productId: string;
  amount: number;
  currency?: string;
  interval?: 'month' | 'year';
  intervalCount?: number;
}

// List all products (admin sees all including inactive)
export async function listProducts(): Promise<PaymentProduct[]> {
  return request<PaymentProduct[]>('/api/squareup/admin/products');
}

// Get a single product
export async function getProduct(productId: string): Promise<PaymentProduct> {
  return request<PaymentProduct>(`/api/squareup/admin/products/${productId}`);
}

// Create a new product
export async function createProduct(input: CreateProductInput): Promise<PaymentProduct> {
  return request<PaymentProduct>('/api/squareup/admin/products', {
    method: 'POST',
    body: input,
  });
}

// Update a product
export async function updateProduct(productId: string, input: UpdateProductInput): Promise<PaymentProduct> {
  return request<PaymentProduct>(`/api/squareup/admin/products/${productId}`, {
    method: 'PATCH',
    body: input,
  });
}

// Delete (deactivate) a product
export async function deleteProduct(productId: string): Promise<void> {
  await request<void>(`/api/squareup/admin/products/${productId}`, {
    method: 'DELETE',
  });
}

// Create a price for a product
export async function createPrice(input: CreatePriceInput): Promise<PaymentPrice> {
  return request<PaymentPrice>('/api/squareup/admin/prices', {
    method: 'POST',
    body: {
      ...input,
      currency: input.currency || 'usd',
    },
  });
}

// Deactivate a price
export async function deactivatePrice(priceId: string): Promise<void> {
  await request<void>(`/api/squareup/admin/prices/${priceId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Admin Coupon Management
// ============================================================================

export type CouponType = 'percent' | 'fixed';

export interface Coupon {
  id: string;
  code: string;
  type: CouponType;
  amountOff?: number;
  percentOff?: number;
  currency: string;
  validFrom?: string;
  validUntil?: string;
  maxRedemptions?: number;
  maxPerUser?: number;
  applicableProducts?: string[];
  active: boolean;
  redemptionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCouponInput {
  code: string;
  type: CouponType;
  amountOff?: number;
  percentOff?: number;
  currency?: string;
  validFrom?: string;
  validUntil?: string;
  maxRedemptions?: number;
  maxPerUser?: number;
  applicableProducts?: string[];
}

export interface UpdateCouponInput {
  validFrom?: string;
  validUntil?: string;
  maxRedemptions?: number;
  maxPerUser?: number;
  applicableProducts?: string[];
  active?: boolean;
}

// List all coupons
export async function listCoupons(includeInactive = false): Promise<Coupon[]> {
  const query = includeInactive ? '?includeInactive=true' : '';
  return request<Coupon[]>(`/api/squareup/admin/coupons${query}`);
}

// Get a single coupon
export async function getCoupon(couponId: string): Promise<Coupon> {
  return request<Coupon>(`/api/squareup/admin/coupons/${couponId}`);
}

// Create a new coupon
export async function createCoupon(input: CreateCouponInput): Promise<Coupon> {
  return request<Coupon>('/api/squareup/admin/coupons', {
    method: 'POST',
    body: input,
  });
}

// Update a coupon
export async function updateCoupon(couponId: string, input: UpdateCouponInput): Promise<Coupon> {
  return request<Coupon>(`/api/squareup/admin/coupons/${couponId}`, {
    method: 'PATCH',
    body: input,
  });
}

// Delete a coupon
export async function deleteCoupon(couponId: string): Promise<void> {
  await request<void>(`/api/squareup/admin/coupons/${couponId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Admin Email Management
// ============================================================================

export interface EmailTemplate {
  id: string;
  type: string;
  name: string;
  subject: string;
  htmlBody: string;
  textBody: string;
  variables: string[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateEmailTemplateInput {
  name?: string;
  subject?: string;
  htmlBody?: string;
  textBody?: string;
  active?: boolean;
}

export interface EmailLog {
  id: string;
  recipientEmail: string;
  recipientName?: string;
  templateType: string;
  subject: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'bounced' | 'complained';
  provider: string;
  messageId?: string;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
  sentAt?: string;
  deliveredAt?: string;
  failedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmailLogsResult {
  logs: EmailLog[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListEmailLogsInput {
  recipient?: string;
  templateType?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

export interface EmailStats {
  totalSent: number;
  delivered: number;
  failed: number;
  bounced: number;
  complained: number;
  pending: number;
  deliveryRate: number;
  bounceRate: number;
  complaintRate: number;
}

export interface WebhookEvent {
  id: string;
  provider: string;
  eventType: string;
  eventId: string;
  payload: Record<string, unknown>;
  status: 'pending' | 'processed' | 'failed';
  processedAt?: string;
  errorMessage?: string;
  emailLogId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookEventsResult {
  events: WebhookEvent[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListWebhookEventsInput {
  provider?: string;
  eventType?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

export interface WebhookStats {
  totalEvents: number;
  processed: number;
  failed: number;
  pending: number;
  eventsByType: Record<string, number>;
}

export interface TemplatePreviewInput {
  templateType: string;
  variables?: Record<string, string>;
}

export interface TemplatePreviewResult {
  subject: string;
  htmlBody: string;
  textBody: string;
}

// Email Templates
export async function getEmailTemplates(): Promise<EmailTemplate[]> {
  return request<EmailTemplate[]>('/api/squareup/admin/email/templates');
}

export async function getEmailTemplate(templateType: string): Promise<EmailTemplate> {
  return request<EmailTemplate>(`/api/squareup/admin/email/templates/${templateType}`);
}

export async function updateEmailTemplate(
  templateType: string,
  input: UpdateEmailTemplateInput
): Promise<EmailTemplate> {
  return request<EmailTemplate>(`/api/squareup/admin/email/templates/${templateType}`, {
    method: 'PUT',
    body: input,
  });
}

export async function previewEmailTemplate(input: TemplatePreviewInput): Promise<TemplatePreviewResult> {
  return request<TemplatePreviewResult>('/api/squareup/admin/email/templates/preview', {
    method: 'POST',
    body: input,
  });
}

// Email Logs
export async function getEmailLogs(input?: ListEmailLogsInput): Promise<EmailLogsResult> {
  const params = new URLSearchParams();
  if (input?.recipient) params.set('recipient', input.recipient);
  if (input?.templateType) params.set('templateType', input.templateType);
  if (input?.status) params.set('status', input.status);
  if (input?.startDate) params.set('startDate', input.startDate);
  if (input?.endDate) params.set('endDate', input.endDate);
  if (input?.page) params.set('page', input.page.toString());
  if (input?.pageSize) params.set('pageSize', input.pageSize.toString());

  const query = params.toString();
  return request<EmailLogsResult>(`/api/squareup/admin/email/logs${query ? `?${query}` : ''}`);
}

export async function getEmailStats(): Promise<EmailStats> {
  return request<EmailStats>('/api/squareup/admin/email/stats');
}

// Webhook Events
export async function getWebhookEvents(input?: ListWebhookEventsInput): Promise<WebhookEventsResult> {
  const params = new URLSearchParams();
  if (input?.provider) params.set('provider', input.provider);
  if (input?.eventType) params.set('eventType', input.eventType);
  if (input?.status) params.set('status', input.status);
  if (input?.startDate) params.set('startDate', input.startDate);
  if (input?.endDate) params.set('endDate', input.endDate);
  if (input?.page) params.set('page', input.page.toString());
  if (input?.pageSize) params.set('pageSize', input.pageSize.toString());

  const query = params.toString();
  return request<WebhookEventsResult>(`/api/squareup/admin/email/webhook-events${query ? `?${query}` : ''}`);
}

export async function getWebhookStats(): Promise<WebhookStats> {
  return request<WebhookStats>('/api/squareup/admin/email/webhook-stats');
}

// ============================================================================
// Admin System Tasks
// ============================================================================

export interface BackfillConnectionsResult {
  onlineConnectionsCreated: number;
  offlineConnectionsCreated: number;
  usersProcessed: number;
  errors: string[];
}

// Trigger backfill of player connections from legacy data
export async function backfillPlayerConnections(): Promise<BackfillConnectionsResult> {
  return request<BackfillConnectionsResult>('/api/squareup/admin/connections/backfill', {
    method: 'POST',
  });
}
